import { useState, useEffect, useRef, useCallback } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { X, Layers, Target, ListTodo } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';
import { useFormGuard } from '../hooks/useFormGuard';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { UnsavedChangesDialog, DraftResumeBanner } from './UnsavedChangesDialog';
import ConvertToCommitmentModal from './commitments/ConvertToCommitmentModal';
import TimeTrackerControl from './tasks/TimeTrackerControl';
import EntityActivityLog from './tasks/EntityActivityLog';
import { TaskCoreFields } from './tasks/TaskCoreFields';
import { SimilarTaskHint } from './tasks/SimilarTaskHint';
import { useSimilarTasks } from '../hooks/useSimilarTasks';
import { TaskScheduleSection } from './tasks/TaskScheduleSection';
import { TaskSubtasksSection } from './tasks/TaskSubtasksSection';
import { TaskAdvancedSection } from './tasks/TaskAdvancedSection';

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function TaskModal({ task, onClose, onSave, isNew, fromTemplate }) {
  useBodyScrollLock();
  const { businesses } = useBusiness();
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'MEDIUM',
    taskType: 'STANDARD',
    estimatedMinutes: 30,
    businessId: businesses[0]?.id || '',
    dueDate: '',
    energy: 'MEDIUM',
    status: 'TODAY',
    schedulePreference: 'today',
    scheduledDateTime: '',
    labels: [],
    subtasks: [],
    recurrenceRule: 'NONE',
    recurrenceEndDate: '',
    delegatedTo: '',
    delegationNotes: '',
    staffId: null,
    context: '',
    commitmentId: null,
    reminderTiming: 'NONE',
  });
  const [twoMinTimer, setTwoMinTimer] = useState(null); // { active: true, seconds: 120 }
  const [twoMinDismissed, setTwoMinDismissed] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const initialFormRef = useRef(null);
  const similarTasks = useSimilarTasks(form?.title, form?.businessId, !!isNew);

  // Track dirty state by comparing current form to initial snapshot
  const isDirty = initialFormRef.current !== null &&
    JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  const getFormData = useCallback(() => form, [form]);
  const setFormDataFromDraft = useCallback((data) => setForm(data), []);

  const { guard, showDialog, discard, cancel, save, saveDraft, hasDraft, restoreDraft, clearDraft } = useFormGuard(isDirty, {
    draftKey: !task ? 'new_task' : null,
    getFormData,
    setFormData: setFormDataFromDraft,
  });

  useEffect(() => {
    if (task && !isNew) {
      setForm({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'MEDIUM',
        taskType: task.taskType || 'STANDARD',
        estimatedMinutes: task.estimatedMinutes || 30,
        businessId: task.businessId || businesses[0]?.id || '',
        dueDate: task.dueDate || '',
        energy: task.energy || 'MEDIUM',
        status: task.status || 'TODAY',
        schedulePreference: task.scheduledDate && task.scheduledDate !== formatDate(new Date()) ? 'pick-datetime' : 'today',
        scheduledDateTime: task.scheduledDate ? `${task.scheduledDate}T09:00` : '',
        labels: task.labels || [],
        subtasks: task.subtasks || [],
        recurrenceRule: task.recurrenceRule || 'NONE',
        recurrenceEndDate: task.recurrenceEndDate || '',
        delegatedTo: task.delegatedTo || '',
        delegationNotes: task.delegationNotes || '',
        staffId: task.staffId || null,
        context: task.context || '',
        commitmentId: task.commitmentIds?.[0] || null,
        reminderTiming: task.reminderTiming || 'NONE',
      });
    }
  }, [task, isNew, businesses]);

  // Capture initial form snapshot for dirty detection
  const formRef = useRef(form);
  formRef.current = form;
  useEffect(() => {
    // Small delay to ensure form state is settled after task load
    const timer = setTimeout(() => {
      initialFormRef.current = { ...formRef.current };
    }, 50);
    return () => clearTimeout(timer);
  }, [task, isNew]);

  const guardedCloseRef = useRef();
  guardedCloseRef.current = () => guard(onClose);
  const guardedClose = useCallback(() => guardedCloseRef.current?.(), []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        guardedClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [guardedClose]);

  // 2-minute timer countdown
  useEffect(() => {
    if (!twoMinTimer?.active) return;
    const interval = setInterval(() => {
      setTwoMinTimer(prev => {
        if (!prev || prev.seconds <= 1) {
          clearInterval(interval);
          toast.success('2 minutes up! Task auto-completed.');
          onSave({ ...form, status: 'COMPLETED' });
          return null;
        }
        return { ...prev, seconds: prev.seconds - 1 };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [twoMinTimer?.active, form, onSave]);

  // Live mirror of timer + actualMinutes so the modal reflects updates without a full save.
  const [trackerState, setTrackerState] = useState({
    timerStartedAt: task?.timerStartedAt || null,
    actualMinutes: task?.actualMinutes || 0,
  });
  useEffect(() => {
    if (task) {
      setTrackerState({
        timerStartedAt: task.timerStartedAt || null,
        actualMinutes: task.actualMinutes || 0,
      });
    }
  }, [task]);

  const handleSaveAsTemplate = async () => {
    if (!form.title.trim()) { toast.error('Enter a title first'); return; }
    try {
      await api.post('/templates', {
        name: form.title,
        description: form.description || '',
        priority: form.priority,
        taskType: form.taskType,
        estimatedMinutes: form.estimatedMinutes,
        energy: form.energy,
        businessId: form.businessId || null,
        labels: form.labels,
        subtasks: (form.subtasks || []).map(s => ({ title: typeof s === 'string' ? s : (s.title || '') })),
        recurrenceRule: form.recurrenceRule,
      });
      toast.success('Saved as template');
    } catch {
      toast.error('Could not save template');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Add a task title to continue');
      return;
    }
    // F1 (centralized): for an existing task, reconcile the commitment-link delta
    // from the picker here, so every TaskModal consumer (Tasks, Today, Calendar)
    // gets edit-time bundling without per-surface wiring. Create path links in
    // the consumer (id doesn't exist until the task is posted).
    if (task && !isNew) {
      const original = task.commitmentIds?.[0] || null;
      if (form.commitmentId !== original) {
        try {
          if (original) {
            await api.delete(`/commitments/${original}/link/task/${task.id}`);
          }
          if (form.commitmentId) {
            await api.post(`/commitments/${form.commitmentId}/link`, { itemId: task.id, itemType: 'task' });
          }
        } catch {
          // Surface the binder breakage instead of swallowing it — a silent
          // link failure leaves the task orphaned from its commitment.
          toast.error('Task saved, but updating its commitment link failed');
        }
      }
    }
    onSave(form);
  };

  const todayDate = formatDate(new Date());
  const tomorrowDate = formatDate(new Date(Date.now() + 24 * 60 * 60 * 1000));

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto" style={{ background: 'var(--pd-overlay)', backdropFilter: 'blur(8px)' }}>
      <div className="flex min-h-full items-center justify-center px-4 py-8" onClick={(e) => { if (e.target === e.currentTarget) guardedClose(); }}>
      <div data-testid={isNew ? 'new-task-modal' : 'edit-task-modal'} role="dialog" aria-modal="true" className="w-full max-w-[560px] rounded-[30px] animate-fade-in overflow-hidden" style={{ background: 'var(--pd-popover)', border: '1px solid var(--pd-popover-border)', boxShadow: 'var(--pd-shadow-3)' }}>
        <div className="flex items-center gap-3 px-6 py-5 border-b" style={{ borderColor: 'var(--pd-border)' }}>
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'color-mix(in srgb, var(--pd-focus) 10%, transparent)' }}>
            <ListTodo className="w-5 h-5" style={{ color: 'var(--pd-focus)' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-bold" style={{ color: 'var(--pd-text)' }}>{isNew ? 'New task' : 'Edit task'}</div>
            <div className="text-[10px]" style={{ color: 'var(--pd-subtle)' }}>{isNew ? 'Added to the desk — schedule it now or keep it in the queue.' : 'Refine the details, schedule, and links.'}</div>
          </div>
          <button data-testid="task-modal-close" onClick={guardedClose} className="pd-icon-button"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!task && hasDraft && (
            <DraftResumeBanner
              draftKey="new_task"
              onRestore={() => { restoreDraft(); clearDraft(); toast.success('Draft restored'); }}
              onDiscard={() => { clearDraft(); toast('Draft discarded'); }}
            />
          )}

          <TaskCoreFields form={form} setForm={setForm} businesses={businesses} />
          {isNew && <SimilarTaskHint matches={similarTasks} />}

          <TaskScheduleSection
            form={form} setForm={setForm}
            todayDate={todayDate} tomorrowDate={tomorrowDate}
            twoMinTimer={twoMinTimer} setTwoMinTimer={setTwoMinTimer}
            twoMinDismissed={twoMinDismissed} setTwoMinDismissed={setTwoMinDismissed}
            onSave={onSave}
          />

          <TaskSubtasksSection form={form} setForm={setForm} />

          <TaskAdvancedSection
            form={form}
            setForm={setForm}
            defaultOpen={Boolean(task && !isNew && (
              (task.taskType && task.taskType !== 'STANDARD')
              || (task.energy && task.energy !== 'MEDIUM')
              || (task.recurrenceRule && task.recurrenceRule !== 'NONE')
              || task.context || task.staffId || task.delegatedTo
              || (task.reminderTiming && task.reminderTiming !== 'NONE')
              || task.commitmentIds?.length
            ))}
          />

          {task && !isNew && (
            <div className="space-y-3" data-testid="task-modal-tracking-section">
              <TimeTrackerControl
                key={task.id}
                taskId={task.id}
                initialStartedAt={trackerState.timerStartedAt}
                actualMinutes={trackerState.actualMinutes}
                onChange={setTrackerState}
              />
              <EntityActivityLog entityType="task" entityId={task.id} />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <div className="flex items-center gap-2 mr-auto">
              <button type="button" onClick={handleSaveAsTemplate} className="flex items-center gap-1 px-3 py-2 rounded-[14px] text-[11px] font-medium transition-colors" style={{ color: 'var(--pd-muted)', border: '1px solid var(--pd-border)' }} title="Save current task as a reusable template">
                <Layers className="w-3 h-3" />Template
              </button>
              {task && !isNew && !(task.commitmentIds?.length) && !form.commitmentId && (
                <button type="button" data-testid="task-convert-commitment-btn" onClick={() => setShowConvert(true)} className="flex items-center gap-1 px-3 py-2 rounded-[14px] text-[11px] font-medium transition-colors" style={{ color: 'var(--pd-muted)', border: '1px solid var(--pd-border)' }} title="Promote this task into a commitment with companion items">
                  <Target className="w-3 h-3" />Commitment
                </button>
              )}
            </div>
            <button type="button" onClick={guardedClose} className="px-4 py-2 rounded-[14px] text-sm font-medium" style={{ color: 'var(--pd-muted)' }}>Cancel</button>
            <button data-testid="task-save-button" type="submit" className="px-5 py-2.5 rounded-[14px] text-sm font-medium text-white btn-primary">{isNew ? 'Create task' : 'Save changes'}</button>
          </div>
        </form>
      </div>
      </div>
      {showConvert && task && (
        <ConvertToCommitmentModal
          source={{ type: 'task', id: task.id, title: form.title, date: form.dueDate || undefined, businessId: form.businessId || undefined }}
          onClose={() => setShowConvert(false)}
          onCreated={() => { setShowConvert(false); onClose(); }}
        />
      )}
      <UnsavedChangesDialog
        open={showDialog}
        onDiscard={() => { clearDraft(); discard(); }}
        onCancel={cancel}
        onSave={() => save(() => onSave(form))}
        onSaveDraft={saveDraft}
      />
    </div>
  );
}
